import st from "lib/utils/index";
import AppSettings from "lib/services/AppSettings";
import BaseDecorator from "./BaseDecorator";
import ProductOrderFieldListOptionDecorator from "./ProductOrderFieldListOptionDecorator";
import ProductOrderFieldPickupPointOptionDecorator from "./ProductOrderFieldPickupPointOptionDecorator";

@BaseDecorator.translates(["name", "customer_hint"])
export default class ProductOrderFieldDecorator extends BaseDecorator {
  static build(data = {}) {
    return new this({
      uuid: st.generateUuid(),
      type: "ProductOrderField::Boolean",
      product_variants_assignation_type: "product_variants_assignation_all",
      active: true,
      order_value_required: false,
      translations: [],
      ...data,
    });
  }

  static get presets() {
    return AppSettings.product_order_field_presets;
  }

  static get types() {
    return [
      "ProductOrderField::Text",
      "ProductOrderField::BigText",
      "ProductOrderField::Country",
      "ProductOrderField::Email",
      "ProductOrderField::Date",
      "ProductOrderField::Boolean",
      "ProductOrderField::List",
      "ProductOrderField::Number",
      "ProductOrderField::PhoneNumber",
    ].map((x) => [x, I18n.humanAttributeName("product_order_field", x)]);
  }

  static get priced_field_types() {
    return ["ProductOrderField::Boolean", "ProductOrderField::List", "ProductOrderField::Number"].map((x) => [
      x,
      I18n.humanAttributeName("product_order_field", x),
    ]);
  }

  static get customer_field_types() {
    return this.types;
  }

  static get_customer_preset_id_or_types({ assignation_type } = {}) {
    return this.presets
      .filter((preset) => assignation_type === undefined || preset.assignation_type === assignation_type)
      .filter((preset) => preset.price_type === "price_none")
      .map((preset) => [
        preset.preset_id,
        I18n.t(`activerecord.attributes.product_order_field.presets.${preset.preset_id}.name`),
      ])
      .concat([["ProductOrderField::Text", I18n.t(`activerecord.attributes.product_order_field.custom_field`)]]);
  }

  static get assignation_types() {
    return [
      "assignation_per_order",
      // "assignation_per_item",
      "assignation_per_person",
    ].map((x) => [x, I18n.humanAttributeName("product_order_field", x)]);
  }

  static get price_type_options() {
    return [
      ["price_per_order", I18n.humanAttributeName("product_order_field", "per_order")],
      // ["price_per_item", I18n.humanAttributeName("product_order_field", "per_item")],
      ["price_per_person", I18n.humanAttributeName("product_order_field", "per_person")],
    ];
  }

  constructor(data) {
    super({
      ...data,
      min_value:
        data.type === "ProductOrderField::Number"
          ? data.min_value === "" || data.min_value === undefined
            ? null
            : data.min_value
          : undefined,
      max_value:
        data.type === "ProductOrderField::Number"
          ? data.max_value === "" || data.max_value === undefined
            ? null
            : data.max_value
          : undefined,
      list_options:
        data.type === "ProductOrderField::List"
          ? data.list_options &&
            data.list_options
              .map((x) => new ProductOrderFieldListOptionDecorator(x))
              .sort((a, b) => a.position - b.position)
          : undefined,
      pickup_point_options:
        data.type === "ProductOrderField::PickupPoint"
          ? data.pickup_point_options &&
            data.pickup_point_options
              .map((x) => new ProductOrderFieldPickupPointOptionDecorator(x))
              .sort((a, b) => a.position - b.position)
          : undefined,
    });
  }

  normalize() {
    if (this.assignation_type === "assignation_per_person") {
      if (this.price_type !== "price_none") {
        this.price_type = "price_per_person";
      }
    }

    if (this.type === "ProductOrderField::List") {
      this.list_options = this.list_options || [
        ProductOrderFieldListOptionDecorator.build({
          price: this.price
            ? {
                value: 0,
                currency: this.price.currency,
              }
            : null,
          position: 0,
        }),
      ];
    }

    if (this.preset) {
      const attributes = { ...this.preset };
      delete attributes.default_attributes;
      delete attributes.extra_attributes;
      Object.assign(this, attributes);
    }
  }

  get preset_id_or_type() {
    return this.preset_id || this.type;
  }
  set preset_id_or_type(preset_id_or_type) {
    if (this.constructor.presets.find((preset) => preset.preset_id === preset_id_or_type)) {
      this.preset_id = preset_id_or_type;
    } else {
      this.preset_id = null;
      this.type = preset_id_or_type;
    }
  }

  get has_pricing() {
    return this.price_type !== "price_none";
  }

  get preset() {
    if (this.preset_id) {
      return this.constructor.presets.find((preset) => preset.preset_id === this.preset_id);
    }
  }

  get name() {
    if (this.type === "ProductOrderField::PickupPoint") {
      return I18n.t("activerecord.attributes.order.pickup_point");
    } else if (this.preset) {
      return I18n.t(`activerecord.attributes.product_order_field.presets.${this.preset_id}.name`);
    } else {
      return this.readTranslatedAttribute("name");
    }
  }

  get frontend_name() {
    if (this.preset) {
      return I18n.t(`activerecord.attributes.product_order_field.presets.${this.preset_id}.frontend_name`, {
        defaultValue: this.name,
      });
    } else {
      return this.name;
    }
  }

  get customer_hint() {
    if (this.preset) {
      return (
        I18n.t(`activerecord.attributes.product_order_field.presets.${this.preset_id}.customer_hint`, {
          defaultValue: "",
        }) || null
      );
    } else {
      return this.readTranslatedAttribute("customer_hint");
    }
  }

  get display_name() {
    return this.frontend_name;
  }

  get display_name_with_price() {
    const price = this.display_price;
    return price ? `${this.display_name} (${price})` : this.display_name;
  }

  get display_price() {
    if (!this.has_pricing) {
      return;
    }
    if (this.type === "ProductOrderField::List" || this.type === "ProductOrderField::PickupPoint") {
      return;
    }
    if (!this.price || !this.price.value) {
      return;
    }
    return [st.formatMoney(this.price), this.price_label].filter((x) => !!x).join(" ");
  }

  get price_label() {
    if (
      this.assignation_type !== "assignation_per_order" &&
      this.price_type === "price_per_order" &&
      !this.multiply_price_by_nights_count
    ) {
      return I18n.t("attributes.x_price_per_order", { x: "" });
    } else if (
      this.assignation_type !== "assignation_per_order" &&
      this.price_type === "price_per_order" &&
      this.multiply_price_by_nights_count
    ) {
      return I18n.t("attributes.x_price_per_order_night", { x: "" });
    } else if (
      this.assignation_type !== "assignation_per_item" &&
      this.price_type === "price_per_item" &&
      !this.multiply_price_by_nights_count
    ) {
      return I18n.t("attributes.x_price_per_item", { x: "" });
    } else if (
      this.assignation_type !== "assignation_per_item" &&
      this.price_type === "price_per_item" &&
      this.multiply_price_by_nights_count
    ) {
      return I18n.t("attributes.x_price_per_item_night", { x: "" });
    } else if (
      this.assignation_type !== "assignation_per_person" &&
      this.price_type === "price_per_person" &&
      !this.multiply_price_by_nights_count
    ) {
      return I18n.t("attributes.x_price_per_person", { x: "" });
    } else if (
      this.assignation_type !== "assignation_per_person" &&
      this.price_type === "price_per_person" &&
      this.multiply_price_by_nights_count
    ) {
      return I18n.t("attributes.x_price_per_person_night", { x: "" });
    }
  }

  isAttributePreset(attr) {
    return this.preset && this.preset[attr] !== undefined;
  }

  isAttributeEditable(attr) {
    return this.preset && !this.isAttributePreset(attr);
  }
}



// WEBPACK FOOTER //
// ./src/lib/decorators/ProductOrderFieldDecorator.js